import ErrorBoundary from 'components/molecules/ErrorBoundary/ErrorBoundary';
import UserInboxModalWrapper from 'components/organisms/UserInboxModal/UserInboxModalWrapper';
import AuthenticationProvider from 'components/templates/AuthenticationProvider/AuthenticationProvider';
import FirebaseProvider from 'components/templates/FirebaseProvider/FirebaseProvider';
import PageContainer from 'components/templates/PageContainer/PageContainer';
import RelayProvider from 'components/templates/RelayProvider';
import { isQmeeMarketingAllowed } from 'components/templates/ThirdPartyTrackingProvider/consentGate';
import TranslationWrapper from 'components/templates/Translation/TranslationWrapper';
import { appWithTranslation, useTranslation } from 'next-i18next';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router.js';
import React, { useEffect } from 'react';
import { Provider as AlertProvider } from 'react-alert';
import nextI18NextConfig from '../../next-i18next.config.js';
import '../styles/globals.scss';
import Toast from '../components/molecules/Toast/Toast';
import { DEFAULT_TIMEOUT } from '../components/molecules/Toast/toastMessageTimeout';

export function App({ Component, pageProps }: AppProps) {
  const { locale, query, pathname } = useRouter();
  const { ready } = useTranslation(['common']);

  const HEADER_HEIGHT = '140px';

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    if (!isQmeeMarketingAllowed()) return;
    if (query.he) {
      window.dataLayer.push({
        'gaw-ecid': query.he.toString(),
      });
    }
    if (query.doi) {
      window.dataLayer.push({
        event: 'CompleteRegistration',
      });
    }
  }, [query]);

  return (
    <React.StrictMode>
      <React.Suspense>
        <AlertProvider
          template={Toast}
          containerStyle={{ marginTop: HEADER_HEIGHT, zIndex: 40 }}
          timeout={DEFAULT_TIMEOUT}
        >
          <FirebaseProvider>
            <AuthenticationProvider>
              <React.Suspense>
                <RelayProvider locale={locale}>
                  {ready && (
                    <PageContainer
                      withoutHeader={pageProps.withoutHeader}
                      withoutHeaderOnMobile={pageProps.withoutHeaderOnMobile}
                      withoutSubNav={pageProps.withoutSubNav}
                      withoutFooter={pageProps.withoutFooter}
                      withoutFooterOnMobile={pageProps.withoutFooterOnMobile}
                      fadedPageBg={pageProps.fadedPageBg}
                      fadedPageBgMobile={pageProps.fadedPageBgMobile}
                      isLandingPage={pageProps.isLandingPage}
                      brandTakeover={pageProps.brandTakeover}
                    >
                      <React.Suspense>
                        <ErrorBoundary fallback={null}>
                          <UserInboxModalWrapper />
                        </ErrorBoundary>
                      </React.Suspense>
                      <React.Suspense>
                        <TranslationWrapper namespace={pageProps.namespace}>
                          <Component {...pageProps} />
                        </TranslationWrapper>
                      </React.Suspense>
                    </PageContainer>
                  )}
                </RelayProvider>
              </React.Suspense>
            </AuthenticationProvider>
          </FirebaseProvider>
        </AlertProvider>
      </React.Suspense>
    </React.StrictMode>
  );
}

export default appWithTranslation(App, nextI18NextConfig as any);
